import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import './CardPricingPage.css';
import AdminLayout from '../AdminLayout';
import {
  useGetCardsPricingQuery,
  useGetPricingStatsQuery,
  useUpdateCardPricingMutation,
} from 'store/services/admin/adminCardPricingApi';
import { AdminCardPricing } from 'types/interfaces/admin/cardPricing';
import InlineEditCell from './components/InlineEditCell';
import QuickFiltersBar from './components/QuickFiltersBar';
import PriceHistoryPanel from './components/PriceHistoryPanel';
import CardArtThumbnail from './components/CardArtThumbnail';

type FilterType = 'all' | 'incomplete' | 'missingAll' | 'missingFoil' | 'manualOverrides';

interface PendingChange {
  usd?: number | null;
  usdFoil?: number | null;
  manualOverride: boolean;
}

const CardPricingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  // State
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const initialFilter = (searchParams.get('filter') as FilterType) || (initialSearch ? 'all' : 'incomplete');
  const [filter, setFilter] = useState<FilterType>(initialFilter);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [editingCell, setEditingCell] = useState<{ cardId: number; field: 'usd' | 'usdFoil' } | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, PendingChange>>(new Map());

  // RTK Query hooks
  const { data: stats, isLoading: statsLoading } = useGetPricingStatsQuery();
  const { data: cardData, isLoading, error, refetch } = useGetCardsPricingQuery({
    page,
    pageSize: 50,
    search: debouncedSearch,
    filter,
    sortBy,
    sortOrder,
  });
  const [updatePricing, { isLoading: isUpdating }] = useUpdateCardPricingMutation();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingCell(null);
        setExpandedRowId(null);
      }
      if (e.ctrlKey && e.key === 's' && pendingChanges.size > 0) {
        e.preventDefault();
        saveAllPending();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChanges]);

  // Handle price change
  const handlePriceChange = useCallback((cardId: number, field: 'usd' | 'usdFoil', value: number | null) => {
    setPendingChanges(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(cardId) || { manualOverride: false };
      newMap.set(cardId, { ...existing, [field]: value });
      return newMap;
    });
  }, []);

  // Handle manual override toggle
  const handleOverrideToggle = useCallback(async (cardId: number, enabled: boolean) => {
    try {
      const card = cardData?.cards.find(c => c.id === cardId);
      if (!card) return;

      await updatePricing({
        cardId,
        body: {
          usd: card.usd,
          usdFoil: card.usdFoil,
          manualOverride: enabled,
          reason: enabled ? 'Admin enabled manual override' : 'Admin disabled manual override',
        },
      }).unwrap();

      toast.success(enabled ? 'Manual override enabled' : 'Manual override disabled');
    } catch (err) {
      toast.error('Failed to update manual override');
    }
  }, [cardData, updatePricing]);

  // Save all pending changes
  const saveAllPending = useCallback(async () => {
    const entries = Array.from(pendingChanges.entries());
    let successCount = 0;
    let errorCount = 0;

    for (const [cardId, changes] of entries) {
      try {
        const card = cardData?.cards.find(c => c.id === cardId);
        await updatePricing({
          cardId,
          body: {
            usd: changes.usd !== undefined ? changes.usd : card?.usd,
            usdFoil: changes.usdFoil !== undefined ? changes.usdFoil : card?.usdFoil,
            manualOverride: changes.manualOverride || card?.isManualOverride || false,
            reason: 'Admin price update',
          },
        }).unwrap();
        successCount++;
      } catch (err) {
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Updated ${successCount} card${successCount > 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} card${errorCount > 1 ? 's' : ''}`);
    }

    setPendingChanges(new Map());
    refetch();
  }, [pendingChanges, cardData, updatePricing, refetch]);

  // Handle sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Toggle expanded row
  const toggleExpand = (cardId: number) => {
    setExpandedRowId(prev => prev === cardId ? null : cardId);
  };

  // Get sort indicator
  const getSortIndicator = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Get pending value for a cell
  const getPendingValue = (cardId: number, field: 'usd' | 'usdFoil', originalValue: number | null): number | null => {
    const pending = pendingChanges.get(cardId);
    if (pending && pending[field] !== undefined) {
      return pending[field] as number | null;
    }
    return originalValue;
  };

  // Check if a card has pending changes
  const hasPendingChanges = (cardId: number): boolean => {
    return pendingChanges.has(cardId);
  };

  return (
    <AdminLayout title="Admin Panel - Card Pricing" currentPath="/admin/cards">
      {/* @ts-expect-error - React 19 types issue with children */}
      <div className="card-pricing-page">
        {/* Stats Cards */}
        <div className="pricing-stats-row">
          <div className="stat-card">
            <div className="stat-value">{statsLoading ? '...' : stats?.totalCards?.toLocaleString() || '0'}</div>
            <div className="stat-label">Total Cards</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-value">{statsLoading ? '...' : stats?.cardsWithIncompletePricing?.toLocaleString() || '0'}</div>
            <div className="stat-label">Incomplete Pricing</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{statsLoading ? '...' : stats?.cardsWithManualOverride?.toLocaleString() || '0'}</div>
            <div className="stat-label">Manual Overrides</div>
          </div>
        </div>

        {/* Search + Quick Filters */}
        <div className="pricing-controls">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search cards by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <QuickFiltersBar
            currentFilter={filter}
            onFilterChange={(f) => {
              setFilter(f as FilterType);
              setPage(1);
            }}
            stats={stats}
          />
        </div>

        {/* Pending Changes Bar */}
        {pendingChanges.size > 0 && (
          <div className="pending-changes-bar">
            <span className="pending-count">{pendingChanges.size} unsaved change{pendingChanges.size > 1 ? 's' : ''}</span>
            <div className="pending-actions">
              <button
                className="btn-save"
                onClick={saveAllPending}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save All (Ctrl+S)'}
              </button>
              <button
                className="btn-discard"
                onClick={() => setPendingChanges(new Map())}
                disabled={isUpdating}
              >
                Discard All
              </button>
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="results-count">
          {cardData?.pagination?.totalCards?.toLocaleString() || 0} cards
          {filter === 'incomplete' && ' with incomplete pricing'}
          {filter === 'missingFoil' && ' missing foil prices'}
          {filter === 'missingAll' && ' missing all prices'}
          {filter === 'manualOverrides' && ' with manual overrides'}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="loading-state">Loading cards...</div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-state">
            Error loading cards. Please try again.
            <button onClick={() => refetch()}>Retry</button>
          </div>
        )}

        {/* Table */}
        {!isLoading && !error && (
          <div className="pricing-table-container">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th className="col-expand"></th>
                  <th className="col-name sortable" onClick={() => handleSort('name')}>
                    Card Name{getSortIndicator('name')}
                  </th>
                  <th className="col-set sortable" onClick={() => handleSort('setName')}>
                    Set{getSortIndicator('setName')}
                  </th>
                  <th className="col-foil">Foil</th>
                  <th className="col-language">Lang</th>
                  <th className="col-price sortable" onClick={() => handleSort('usd')}>
                    Value{getSortIndicator('usd')}
                  </th>
                  <th className="col-price sortable" onClick={() => handleSort('usdFoil')}>
                    Foil Value{getSortIndicator('usdFoil')}
                  </th>
                  <th className="col-override"></th>
                </tr>
              </thead>
              <tbody>
                {cardData?.cards?.map((card: AdminCardPricing) => (
                  <React.Fragment key={card.id}>
                    <tr className={`${expandedRowId === card.id ? 'expanded' : ''} ${hasPendingChanges(card.id) ? 'has-pending' : ''}`}>
                      <td className="col-expand">
                        <button
                          className="expand-btn"
                          onClick={() => toggleExpand(card.id)}
                          title={expandedRowId === card.id ? 'Collapse' : 'Expand'}
                        >
                          {expandedRowId === card.id ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className="col-name">
                        <div className="card-name-cell">
                          <CardArtThumbnail scryfallId={card.scryfallId || card.scryfallID || ''} cardName={card.name} />
                          <span className="card-name-text">{card.name}</span>
                        </div>
                      </td>
                      <td className="col-set">
                        <span className="set-name">{card.setName}</span>
                        <span className="set-code">({card.setCode?.toUpperCase()})</span>
                      </td>
                      <td className="col-foil">
                        {card.finishes?.includes('foil') ? (
                          <span className="foil-indicator">✓</span>
                        ) : (
                          <span className="no-foil">—</span>
                        )}
                      </td>
                      <td className="col-language">
                        {card.language?.toUpperCase() || 'EN'}
                      </td>
                      <td className="col-price">
                        <InlineEditCell
                          value={getPendingValue(card.id, 'usd', card.usd)}
                          isEditing={editingCell?.cardId === card.id && editingCell?.field === 'usd'}
                          isPending={hasPendingChanges(card.id)}
                          finishNotAvailable={!card.finishes?.includes('nonfoil')}
                          onStartEdit={() => setEditingCell({ cardId: card.id, field: 'usd' })}
                          onChange={(val) => handlePriceChange(card.id, 'usd', val)}
                          onSave={() => setEditingCell(null)}
                          onCancel={() => setEditingCell(null)}
                        />
                      </td>
                      <td className="col-price">
                        <InlineEditCell
                          value={getPendingValue(card.id, 'usdFoil', card.usdFoil)}
                          isEditing={editingCell?.cardId === card.id && editingCell?.field === 'usdFoil'}
                          isPending={hasPendingChanges(card.id)}
                          finishNotAvailable={!card.finishes?.includes('foil')}
                          onStartEdit={() => setEditingCell({ cardId: card.id, field: 'usdFoil' })}
                          onChange={(val) => handlePriceChange(card.id, 'usdFoil', val)}
                          onSave={() => setEditingCell(null)}
                          onCancel={() => setEditingCell(null)}
                        />
                      </td>
                      <td className="col-override">
                        {card.isManualOverride && (
                          <span className="manual-badge" title="Manual Override Active">M</span>
                        )}
                      </td>
                    </tr>
                    {expandedRowId === card.id && (
                      <tr className="expanded-row">
                        <td colSpan={8}>
                          <PriceHistoryPanel
                            cardId={card.id}
                            isManualOverride={card.isManualOverride}
                            onToggleOverride={(enabled) => handleOverrideToggle(card.id, enabled)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty States */}
        {!isLoading && !error && cardData?.cards?.length === 0 && searchTerm && (
          <div className="empty-state">
            No cards found matching "{searchTerm}"
          </div>
        )}
        {!isLoading && !error && cardData?.cards?.length === 0 && !searchTerm && filter === 'incomplete' && (
          <div className="empty-state success">
            All cards have complete pricing. Great work!
          </div>
        )}

        {/* Pagination */}
        {cardData?.pagination && cardData.pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {cardData.pagination.currentPage} of {cardData.pagination.totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= cardData.pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CardPricingPage;
